import { Component, ElementRef, EventEmitter, Input, NgZone, OnInit, Output, inject } from '@angular/core';
import { IPayPalConfig, NgxPayPalModule } from 'ngx-paypal';
import { firstValueFrom } from 'rxjs';
import { WIDGET_CONSTANTS } from '../../../constants/widget-constants';
import { PayPalOrderDetailModel, PayPalSettingsDbModel } from '../../../db-models/partner-setting.model';
import { CartItem, EventCartItem, GuestModel } from '../../../models/cart.model';
import { Partner } from '../../../models/global';
import { UserState } from '../../../models/state.model';
import { CouponDbModel } from '../../../db-models/coupon-db.model';
import { AppointmentService } from '../../../services/appointment.service';
import { CustomEventService } from '../../../services/custom-event.service';
import { LoggerService } from '../../../services/logger.service';
import { PaypalOrderItem } from '../../../models/paypal.model';
import { PaymentService } from '../../../services/payment.service';
import { isVatAddedOnTop } from '../../../services/vat-calculation.util';

@Component({
  selector: 'app-cw-paypal',
  templateUrl: './cw-paypal.component.html',
  imports: [NgxPayPalModule]
})
export class CwPaypalComponent implements OnInit {

  @Input() paypalSetting: PayPalSettingsDbModel;
  @Input() price: number;
  @Input() discount: number;
  @Input() partner: Partner;
  @Input() cart: CartItem[];
  @Input() appointmentServicesAvailable: any;
  @Input() eventCart: EventCartItem[];
  @Input() widgetType: string;
  @Input() additionalGuests: GuestModel[] = [];
  @Input() anonymousGuestCount = 0;
  @Input() userState: UserState;
  @Input() coupon: CouponDbModel;
  @Input() finalCouponDiscount: number;
  @Input() addonsPrice = 0;

  @Output() paypalSuccessfulEvent = new EventEmitter<PayPalOrderDetailModel>();
  @Output() resetPaypalEvent = new EventEmitter<any>();
  @Output() paypalOrderStartedEvent = new EventEmitter<void>();
  @Output() paypalCancelledEvent = new EventEmitter<void>();

  payPalConfig: IPayPalConfig;
  showSuccess = false;
  showCancel = false;
  showError = false;
  private paypalPaymentLogId: string;
  // Cached PayPal order id — reused across price changes. Option A pattern:
  // instead of creating a new order per change, we PATCH this one.
  private paypalOrderId: string;

  private zone = inject(NgZone);
  private hostElementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  constructor(
    private customEventService: CustomEventService,
    private appointmentService: AppointmentService,
    private paymentService: PaymentService,
  ) { }

  ngOnInit(): void {
    this.customEventService.resetPayPalOrderDetailEvent.subscribe({
      next: () => {
        this.discount = undefined;
        this.payPalConfig = undefined;
        setTimeout(() => {
          this.initConfig();
        }, 300);
      }
    });

    // Option A: items, discount, addons are read fresh inside createOrderOnServer
    // on each button click, so no unmount/reinit is needed — just update state.
    // Existing PayPal order (if any) gets PATCHed with the latest values.
    this.customEventService.cartItemsChangeEvent.subscribe({
      next: (result: {
        cart?: CartItem[],
        eventCart?: EventCartItem[],
        type: string,
      }) => {
        this.discount = undefined;
        if (result.type === WIDGET_CONSTANTS.APPOINTMENT) {
          this.cart = result.cart;
        } else {
          this.eventCart = result.eventCart;
        }
      }
    });

    this.customEventService.couponChangeEvent.subscribe({
      next: (couponDiscount: number) => {
        if (this.widgetType === WIDGET_CONSTANTS.APPOINTMENT || this.widgetType === WIDGET_CONSTANTS.COMPACT) {
          this.discount = couponDiscount;
        }
      }
    });

    this.customEventService.addonsPriceChangeEvent.subscribe({
      next: (addonsPrice: number) => {
        if (this.widgetType === WIDGET_CONSTANTS.APPOINTMENT || this.widgetType === WIDGET_CONSTANTS.COMPACT) {
          this.addonsPrice = addonsPrice;
        }
      }
    });

    this.customEventService.additionalGuestChangeEvent.subscribe({
      next: (result: {
        finalPrice: number,
        discount: number,
        additionalGuests: GuestModel[]
      }) => {
        if (this.discount === result.discount && this.price === result.finalPrice && this.additionalGuests?.length === result.additionalGuests?.length) {
          return;
        }
        this.discount = result.discount;
        this.price = result.finalPrice;
        this.additionalGuests = result.additionalGuests;
      }
    });

    this.initConfig();
  }

  private initConfig(): void {
    this.payPalConfig = {
      currency: this.partner.currency,
      clientId: this.paypalSetting.client_id,
      createOrderOnServer: () => {
        // Compute items fresh on each click so coupon/addon changes are reflected
        // without needing to unmount-reinit the SDK (flicker-free UX).
        let items: PaypalOrderItem[];
        if (this.widgetType === WIDGET_CONSTANTS.APPOINTMENT || this.widgetType === WIDGET_CONSTANTS.COMPACT) {
          items = this.getAppointmentPaypalItems();
        } else {
          items = this.getEventPaypalItems();
        }

        // Option A: if an order already exists (customer clicked before, or we have
        // a cached order_id from a previous coupon/addon change), PATCH it instead
        // of creating a new one. Mirrors Stripe's PaymentIntent update pattern.
        const orderDiscount = (this.finalCouponDiscount > 0)
          ? this.finalCouponDiscount
          : (this.discount > 0 ? this.discount : 0);
        const serviceNames = items.map((item: PaypalOrderItem) => item.name).join(', ');
        const tax = this.getVatAddedOnTop(items, orderDiscount);
        const payload = {
          amount: this.price,
          currency: this.partner.currency,
          items: items,
          discount: orderDiscount,
          ...(tax > 0 ? { tax } : {}),
          metadata: {
            partner_id: this.partner.id,
            booking_name: this.partner.bookingName,
            widget_type: this.widgetType,
            services: serviceNames,
            email: this.userState?.eMail || '',
            prename: this.userState?.forename || '',
            lastname: this.userState?.lastName || '',
            phone: this.userState?.phone || '',
            mobile: this.userState?.mobile || '',
            ...(this.addonsPrice > 0 ? { addons_price: this.addonsPrice } : {}),
            ...(this.coupon ? {
              coupon_code: this.coupon.code,
              is_fixed_amount: this.coupon.is_fixed_amount?.toString(),
              is_percentage: this.coupon.is_percentage?.toString(),
              coupon_amount: this.coupon.fixed_amount,
              coupon_percentage: this.coupon.percentage,
              coupon_final_discount: this.finalCouponDiscount,
            } : {}),
          },
        };

        if (this.paypalOrderId && this.paypalPaymentLogId) {
          LoggerService.log('[Paypal debug] patching existing order — order_id:', this.paypalOrderId);
          return firstValueFrom(
            this.paymentService.updatePaypalOrder({
              ...payload,
              order_id: this.paypalOrderId,
              log_uuid: this.paypalPaymentLogId,
            })
          ).then(() => this.paypalOrderId)
            .catch(err => {
              // Patch failed (e.g. order already approved/expired) — fall back to fresh create
              LoggerService.warn('[Paypal debug] patch failed, creating fresh order', err);
              this.paypalOrderId = undefined;
              this.paypalPaymentLogId = undefined;
              return firstValueFrom(this.paymentService.createPaypalOrder(payload)).then(response => {
                this.paypalPaymentLogId = response.log_uuid;
                this.paypalOrderId = response.order_id;
                return response.order_id;
              });
            });
        }

        LoggerService.log('[Paypal debug] creating new order — amount:', this.price, 'discount:', orderDiscount);
        return firstValueFrom(this.paymentService.createPaypalOrder(payload)).then(response => {
          LoggerService.log('[Paypal debug] created — log_uuid:', response.log_uuid, 'order_id:', response.order_id);
          this.paypalPaymentLogId = response.log_uuid;
          this.paypalOrderId = response.order_id;
          return response.order_id;
        });
      },
      advanced: {
        commit: 'true'
      },
      style: {
        color: 'gold',
        shape: 'rect',
      },
      onApprove: (data, actions) => {
        LoggerService.log('[Paypal debug] onApprove - transaction was approved, but not authorized', data, actions);
        actions.order.get().then(details => {
          LoggerService.log('[Paypal debug] onApprove - you can get full order details inside onApprove: ', details);
        });
      },
      onClientAuthorization: (data: any) => {
        LoggerService.log('[Paypal debug] onClientAuthorization - completed transaction', data);
        this.showSuccess = true;
        const newPayPalOrderDetailModel: PayPalOrderDetailModel = {
          paypal_order_id: data.id,
          paypal_payer_id: data.payer ? data.payer.payer_id : null,
          paypal_status: data.status,
          paypal_raw_data: JSON.stringify(data),
          paypal_capture_id: undefined,
          paypal_payment_log_id: this.paypalPaymentLogId,
        };

        if (data?.purchase_units && data?.purchase_units[0]) {
          if (data?.purchase_units[0]?.payments
            && data?.purchase_units[0]?.payments?.captures
            && data?.purchase_units[0]?.payments?.captures[0]) {
            newPayPalOrderDetailModel.paypal_capture_id = data?.purchase_units[0]?.payments?.captures[0].id;
          }
        }

        LoggerService.log('[Paypal debug] newPayPalOrderDetailModel ', newPayPalOrderDetailModel);

        // Order consumed by successful capture — clear cached id so a fresh
        // order is created for any subsequent booking attempt.
        this.paypalOrderId = undefined;
        this.paypalPaymentLogId = undefined;
        this.paypalSuccessfulEvent.emit(
          newPayPalOrderDetailModel
        );
      },
      onCancel: (data, actions) => {
        LoggerService.log('[Paypal debug] OnCancel', data, actions);
        // PayPal SDK callbacks run outside Angular zone — wrap emits so
        // change detection runs and the parent's isPaymentInProgress flag
        // update is reflected in the UI (e.g. cancel button hides).
        this.zone.run(() => {
          this.showCancel = true;
          this.paypalCancelledEvent.emit();
        });
      },
      onError: err => {
        LoggerService.log('[Paypal debug] OnError', err);
        this.zone.run(() => {
          this.showError = true;
          this.paypalCancelledEvent.emit();
        });
      },
      onClick: (data, actions) => {
        LoggerService.log('[Paypal debug] onClick', data, actions);
        this.zone.run(() => {
          this.resetStatus();
          this.paypalOrderStartedEvent.emit();
        });
      }
    };
  }

  resetStatus(): void {
    this.showError = false;
    this.showSuccess = false;
    this.showCancel = false;
  }

  // The items are net, so the VAT added on top is what the price holds beyond them. PayPal gets it
  // as tax_total; inside item_total the order would not add up and PayPal refuses it.
  private getVatAddedOnTop(items: PaypalOrderItem[], discount: number): number {
    if (!isVatAddedOnTop(this.partner?.widget_vat_type)) {
      return 0;
    }
    const itemsCents = items.reduce(
      (sum: number, item: PaypalOrderItem) => sum + Math.round(Number(item.unit_amount?.value) * 100) * Number(item.quantity || 1),
      0
    );
    const taxCents = Math.round(Number(this.price) * 100) + Math.round(Number(discount) * 100) - itemsCents;
    return taxCents > 0 ? taxCents / 100 : 0;
  }

  getAppointmentPaypalItems(): any[] {
    const items: {
      name: string,
      quantity: number,
      unit_amount: {
        value: string,
        currency_code: string,
      }
    }[] = [];

    for (const cartItem of this.cart) {
      const services: any[] = this.getServicesByIds({ serviceIds: cartItem.servicesIds });
      const servicesList: string[] = [];
      if (services && services.length > 0) {
        for (const service of services) {
          servicesList.push(service?.name);
        }
      }
      if ((cartItem.total.price / services.length) > 0) {
        items.push({
          name: servicesList.join(','),
          quantity: 1,
          unit_amount: {
            value: cartItem.total.price,
            currency_code: this.partner.currency
          }
        });
      } else {
        items.push({
          name: servicesList.join(','),
          quantity: 1,
          unit_amount: {
            value: 0 + '',
            currency_code: this.partner.currency
          }
        });
      }
    }

    // Append addons (custom field add-on prices) as a line item so PayPal
    // breakdown reconciles: item_total - discount == amount.
    if (this.addonsPrice > 0) {
      items.push({
        name: 'Additional options',
        quantity: 1,
        unit_amount: {
          value: String(this.addonsPrice),
          currency_code: this.partner.currency,
        },
      });
    }

    LoggerService.log('[Paypal debug] Final paypal items ', items);

    return items;
  }

  getServicesByIds({ serviceIds, returnKey = 'services' }: { serviceIds: number[]; returnKey?: string }) {
    return this.appointmentService.getServicesByIds({ serviceIds, returnKey }, this.appointmentServicesAvailable);
  }

  getEventPaypalItems(): any[] {
    const items: {
      name: string,
      quantity: number,
      unit_amount: {
        value: string,
        currency_code: string,
      }
    }[] = [];

    // Every participant pays the event price: the booker, the named guests and the anonymous ones.
    const quantity = 1 + (this.additionalGuests?.length || 0) + Math.max(0, Math.trunc(Number(this.anonymousGuestCount) || 0));

    for (const cartItem of this.eventCart) {
      if (cartItem.currentEvent?.price > 0) {
        items.push({
          name: cartItem.currentEvent.title,
          quantity: quantity,
          unit_amount: {
            value: cartItem.currentEvent?.price + '',
            currency_code: this.partner.currency
          }
        });
      } else {
        items.push({
          name: cartItem.currentEvent.title,
          quantity: quantity,
          unit_amount: {
            value: 0 + '',
            currency_code: this.partner.currency
          }
        });
      }
    }

    // Same as the appointment items: without the add-ons the breakdown does not add up and the
    // difference would land in tax_total. Every participant pays them, like the event price.
    if (this.addonsPrice > 0) {
      items.push({
        name: 'Additional options',
        quantity: quantity,
        unit_amount: {
          value: String(this.addonsPrice),
          currency_code: this.partner.currency,
        },
      });
    }

    return items;
  }

  onPaypalScriptLoaded(event: any): void {
    LoggerService.log('[Paypal debug] Is paypal script loaded', event);
    this.resolveRenderTargetInsideShadowRoot(event);
  }

  /**
   * Shadow DOM web component only. ngx-paypal renders the buttons with
   * `paypal.Buttons(config).render('#<containerId>')`, and the PayPal SDK resolves that selector via
   * `document.querySelector` — which cannot see into the widget's shadow tree, so the buttons would
   * never render. The SDK equally accepts the container ELEMENT, so `Buttons` is wrapped once (the
   * `scriptLoaded` hook fires before ngx-paypal's render): a string target that resolves inside this
   * component's shadow root is replaced by that element; every other target — the host page's own
   * PayPal usage included — passes through untouched. Re-applied automatically when ngx-paypal
   * reloads the SDK (config change), because `scriptLoaded` fires again with the fresh global.
   */
  private resolveRenderTargetInsideShadowRoot(payPal: any): void {
    const root = this.hostElementRef.nativeElement.getRootNode();
    if (!payPal?.Buttons || typeof ShadowRoot === 'undefined' || !(root instanceof ShadowRoot) || payPal.Buttons.__calensoShadowShim) {
      return;
    }
    const originalButtons = payPal.Buttons;
    const wrappedButtons = (buttonsConfig: unknown) => {
      const buttons = originalButtons(buttonsConfig);
      const originalRender = buttons.render.bind(buttons);
      buttons.render = (target: unknown) => originalRender((typeof target === 'string' && root.querySelector(target)) || target);
      return buttons;
    };
    Object.assign(wrappedButtons, originalButtons);   // keep SDK statics (e.g. Buttons.driver)
    wrappedButtons.__calensoShadowShim = true;
    payPal.Buttons = wrappedButtons;
  }
}
